import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import _every from 'lodash/every';
import _find from 'lodash/find';
import _includes from 'lodash/includes';

import { reorderSeats } from '../../util/seatingChartUtils';

import Seat from '../Seat/Seat';
import './Table.styles.less';

const Table = ({
  activeSeatInputUUID,
  index,
  draggingSeatInProgress,
  guestsByID,
  moveTable,
  onEditTable,
  onDragTable,
  onReorderTable,
  onReorderSeats, // <- pass in updated seat array
  onReorderSeat,
  onSelectGuest,
  pushSeat,
  removeSeat,
  seats,
  seatsByUUID,
  selectedGuests,
  singleDragOriginTable,
  table,
  tablesOfSelectedGuests,
  hideBanner,
  showBanner,
  onEndDrag,
  onStartDrag,
  flowRunId,
  updateFlowRunId,
}) => {
  const tableUUID = table.uuid;
  const areSeatsAvailable = () => {
    const numSelectedGuests = selectedGuests.guests.length + selectedGuests.couple.length;
    return numSelectedGuests + table.num_filled_seats <= table.num_seats;
  };

  const isAddingPeople = () =>
    tablesOfSelectedGuests.length > 0 && !_includes(tablesOfSelectedGuests, tableUUID);

  const isReorderingSeats = () =>
    tablesOfSelectedGuests.length > 0 && _every(tablesOfSelectedGuests, uuid => uuid === tableUUID);

  const addSelectedGuestsToTable = () => {
    if (areSeatsAvailable()) {
      pushSeat(tableUUID);
    } else {
      const { num_filled_seats: filledSeats, num_seats: numSeats, name } = table;
      const isFull = filledSeats === numSeats;
      const bannerID = Date.now().toString().slice(9); // add ID to banners, so old banner timeouts don't kill new banners
      const text = isFull
        ? `You've added the maximum number of guests to ${name}.`
        : `There aren't enough empty seats at ${name} to add the guests you selected.`;
      showBanner(text, 'error', bannerID);
      setTimeout(() => hideBanner(bannerID), 2600);
    }
  };

  const moveSelectedGuestsToSeatIndex = targetIndex => {
    const seatObjArray = seats.map(uuid => seatsByUUID[uuid]);
    const allSelected = selectedGuests.couple.concat(selectedGuests.guests);
    const selectedSeatUUIDs = allSelected.map(key =>
      _find(
        seats,
        seat => seatsByUUID[seat].guest_id === key || seatsByUUID[seat].couple_role === key
      )
    );
    const selectedSet = selectedSeatUUIDs.map(uuid => ({
      key:
        seatsByUUID[uuid].guest_id !== null
          ? seatsByUUID[uuid].guest_id
          : seatsByUUID[uuid].couple_role,
      displayName: seatsByUUID[uuid].display_name,
      seatUUID: uuid,
    }));
    onReorderSeats(reorderSeats(seatObjArray, targetIndex, selectedSet));
  };
  const receiveSeat = targetIndex => {
    if (isAddingPeople()) {
      addSelectedGuestsToTable();
    } else if (isReorderingSeats() && targetIndex !== null) {
      moveSelectedGuestsToSeatIndex(targetIndex);
    }
  };
  const [{ isDragging }, dragRef] = useDrag({
    item: { type: 'TABLE' },
    begin() {
      onDragTable();
      return { item: 'table', table, index };
    },
    end() {
      onEndDrag();
    },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, dropRef] = useDrop({
    accept: ['SEAT', 'TABLE'],
    drop(item, monitor) {
      const sourceObj = monitor.getItem();
      if (sourceObj.item === 'table') moveTable();
      if (!monitor.didDrop()) receiveSeat(null);
    },
    hover(item, monitor) {
      const sourceObject = monitor.getItem();
      if (sourceObject.item === 'table') {
        const dragIndex = sourceObject.index;
        const hoverIndex = index;
        const sourceTableUuid = sourceObject.table.uuid;
        if (dragIndex === hoverIndex) return;
        if (tableUUID !== sourceTableUuid) {
          onReorderTable(dragIndex, hoverIndex);
          // eslint-disable-next-line no-param-reassign
          monitor.getItem().index = hoverIndex;
        }
      }
    },
    collect: monitor => ({ isOver: monitor.isOver() }),
  });

  const opacity = isDragging ? 0 : 1;
  const showInsertion = isReorderingSeats() && draggingSeatInProgress;
  const showHighlight = isAddingPeople() && isOver && areSeatsAvailable();
  const tableClass = cx('sc__table', {
    'sc__table--is-dragged': isDragging,
    table__highlighted: showHighlight,
    'table__can-reorder': showInsertion,
  });

  return (
    <div className={tableClass} ref={node => dragRef(dropRef(node))}>
      <div style={{ opacity }} className="sc__table__header">
        <div className="sc__table__header__title-group">
          <h3 className="sc__table__header__title">{table.name}</h3>
          {/* <h4 className="sc__table__header__sub-title">The Happy Couple</h4> */}
        </div>
        <div className="sc__table__header__action-group">
          <button className="sc__table__header__edit-button" onClick={() => onEditTable(table)}>
            <i className="zolaicon zolaicon-gear" />
          </button>
        </div>
      </div>
      <div className="sc__table__content" style={{ opacity }}>
        {seats.map((uuid, i) => {
          const seat = seatsByUUID[uuid];
          const { guest_id: guestID } = seat;
          const isSelected =
            seat.couple_role != null
              ? selectedGuests.couple.indexOf(seat.couple_role) > -1
              : selectedGuests.guests.indexOf(seat.guest_id) > -1;
          const partOfDrag = isSelected && draggingSeatInProgress;
          const canReorder = singleDragOriginTable === seat.table_uuid;
          const hasDeclined =
            guestID !== null && Object.prototype.hasOwnProperty.call(guestsByID, guestID)
              ? guestsByID[guestID].rsvpType === 'DECLINED'
              : false;
          const className = cx('sc__table__seat', {
            'sc__table__seat--is-selected': isSelected && !seat.isEmpty,
            'sc__empty-seat': seat.isEmpty,
            'sc__table__seat--is-dragged': partOfDrag,
          });
          // The additional seat.display_name prop passed to a seat is to ensure a re-render.
          // For some reason when clicking + placing, it doesn't update, despite the seat changing
          return (
            <Seat
              key={`seat-${seat.uuid}`}
              canReorder={canReorder}
              className={className}
              flowRunId={flowRunId}
              rsvpDeclined={hasDeclined}
              index={i}
              isFormActive={activeSeatInputUUID === seat.uuid}
              partOfDrag={partOfDrag}
              seat={seat}
              tableUUID={tableUUID}
              updateFlowRunId={updateFlowRunId}
              handlers={{
                onReceiveSeat: receiveSeat,
                onEndDrag,
                onStartDrag,
                onSelectGuest,
                onRemoveSeat: removeSeat,
                onReorderSeat,
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

Table.propTypes = {
  activeSeatInputUUID: PropTypes.string,
  index: PropTypes.number,
  table: PropTypes.shape({
    name: PropTypes.string,
    num_seats: PropTypes.number,
    num_filled_seats: PropTypes.number,
    uuid: PropTypes.string,
  }),
  singleDragOriginTable: PropTypes.string,
  seats: PropTypes.arrayOf(PropTypes.string),
  seatsByUUID: PropTypes.objectOf(PropTypes.any),
  draggingSeatInProgress: PropTypes.bool,
  selectedGuests: PropTypes.shape({
    couple: PropTypes.array,
    guests: PropTypes.array,
  }),
  tablesOfSelectedGuests: PropTypes.arrayOf(PropTypes.string),
  onDragTable: PropTypes.func,
  onStartDrag: PropTypes.func,
  onEndDrag: PropTypes.func,
  pushSeat: PropTypes.func,
  removeSeat: PropTypes.func,
  onReorderSeats: PropTypes.func, // <- pass in seat array
  onReorderSeat: PropTypes.func,
  onEditTable: PropTypes.func,
  onSelectGuest: PropTypes.func,
  moveTable: PropTypes.func,
  onReorderTable: PropTypes.func,
  hideBanner: PropTypes.func,
  showBanner: PropTypes.func,
  flowRunId: PropTypes.string,
  updateFlowRunId: PropTypes.func,
};

export default Table;



// WEBPACK FOOTER //
// ./src/components/manage/seatingchart/Tables/Table/Table.jsx